import type { AxiosInstance } from 'axios'
import type { RationType, RationTagType, RationDetailsType, MealTypeType } from 'src/types/rations'
import type { ListResponse } from 'src/types/response'
import type { PaginationRequestParams } from 'src/types/api'
import type { RecipeType, UserRecipeBody, UserRecipeType } from 'src/types/recipes'


export type RationParams = PaginationRequestParams & {
  min_calories?: number | undefined,
  max_calories?: number | undefined,
  tags?: string | undefined,
}

export type RecipeParams = PaginationRequestParams & {
  min_calories?: number | undefined,
  max_calories?: number | undefined,
  meal_type?:  number | undefined,
  show_in_common_list?: boolean | undefined,
}

export class NutritionApi {
  api: AxiosInstance

  urls = {
    mealTypes: () => 'meal-types/' as const,
    rationTags: () => 'ration-tags/' as const,
    rations: () => 'rations/' as const,
    ration: (slug: RationType['slug']) => `rations/${slug}/`,
    recipes: () => 'recipes/' as const,
    recipe: (slug: RecipeType['slug']) => `recipes/${slug}/`,
    userRecipe: (slug: UserRecipeType['slug']) => `users-recipes/${slug}/` as const,
    userRecipes: () => 'users-recipes/' as const,
  }

  constructor (api: AxiosInstance) {
    this.api = api
  }

  getMealTypes = async (): Promise<MealTypeType[]> => {
    const response = await this.api.get<MealTypeType[]>(this.urls.mealTypes())
    return response.data
  }

  getRationTags = async (): Promise<RationTagType[]> => {
    const response = await this.api.get<RationTagType[]>(this.urls.rationTags())
    return response.data
  }

  getRations = async (params: RationParams): Promise<ListResponse<RationType>> => {
    const response = await this.api.get<ListResponse<RationType>>(this.urls.rations(), { params })
    return response.data
  }

  getRation = async (slug: RationType['slug']): Promise<RationDetailsType> => {
    const response = await this.api.get<RationDetailsType>(this.urls.ration(slug))
    return response.data
  }

  getRecipe = async (slug: RecipeType['slug']): Promise<RecipeType> => {
    const response = await this.api.get<RecipeType>(this.urls.recipe(slug))
    return response.data
  }

  getRecipes = async (params: RecipeParams): Promise<ListResponse<RecipeType>> => {
    const response = await this.api.get<ListResponse<RecipeType>>(this.urls.recipes(), { params })
    return response.data
  }

  getUserRecipe = async (slug: UserRecipeType['slug']): Promise<UserRecipeType> => {
    const response = await this.api.get<UserRecipeType>(this.urls.userRecipe(slug))
    return response.data
  }

  getUserRecipes = async (): Promise<UserRecipeType[]> => {
    const response = await this.api.get<UserRecipeType[]>(this.urls.userRecipes())
    return response.data
  }

  patchUserRecipe = async (slug: UserRecipeType['slug'], body: UserRecipeBody): Promise<UserRecipeType> => {
    const response = await this.api.patch<UserRecipeType>(this.urls.userRecipe(slug), body)
    return response.data
  }

  postUserRecipe = async (body: UserRecipeBody): Promise<UserRecipeType> => {
    const response = await this.api.post<UserRecipeType>(this.urls.userRecipes(), body)
    return response.data
  }
}
